import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'
import type { MonetizationReportResponse, PayoutRecord } from './agentsApi'

export interface DeveloperPayoutSummary {
  totalEarnedPaise: number
  totalPaidPaise: number
  outstandingPaise: number
}

export interface BankDetails {
  accountHolderName: string
  accountNumberMasked: string
  ifscCode: string
  bankName: string
  panNumber: string | null
  updatedAt: string
}

export interface AdminBankDetails {
  accountHolderName: string
  accountNumber: string
  ifscCode: string
  bankName: string
  panNumber: string | null
  updatedAt: string
  // True when the stored account number couldn't be decrypted (e.g. encrypted
  // under a different server secret) — accountNumber is a placeholder, not real data.
  decryptFailed: boolean
}

export interface DeveloperApplication {
  id: string
  userId: string
  orgName: string
  website: string | null
  reason: string
  status: 'pending' | 'approved' | 'rejected'
  reviewedAt: string | null
  reviewedBy: string | null
  reviewNote: string | null
  termsAgreedAt: string | null
  createdAt: string
  updatedAt: string
  apiKeys: DeveloperApiKey[]
  agents: ExternalAgent[]
}

export interface DeveloperApiKey {
  id: string
  name: string
  keyPrefix: string
  lastUsedAt: string | null
  isActive: boolean
  createdAt: string
  rawKey?: string // only present immediately after creation
}

export interface ExternalAgent {
  id: string
  name: string
  description: string
  agentType: 'external' | 'hosted'
  externalEndpoint: string | null
  categoryIds: string[]
  suggestedCategoryName: string | null
  status: string
  icon: string | null
  color: string | null
  avatarUrl: string | null
  reviewNote: string | null
  resubmittedAt: string | null
  healthStatus: string | null
  healthLatencyMs: number | null
  healthCheckedAt: string | null
  hostedProvider: string | null
  hostedModel: string | null
  hasApiKey: boolean
  systemPrompt: string | null
  signingSecret?: string // only present immediately after creation
  createdAt: string
  updatedAt: string
}

export interface AdminDeveloperApplication extends DeveloperApplication {
  user: { userId: string; displayName: string; avatarUrl: string | null } | null
  activeKeyCount: number
  agentCount: number
}

export interface MessageUnlockInvoice {
  id: string
  invoiceNumber: string
  issuedAt: string
  totalPaise: number
  isPlaceholder: boolean
  agentPaise: number
}

export interface KnowledgeFile {
  id: string
  filename: string
  fileSizeBytes: number
  status: 'processing' | 'ready' | 'failed'
  chunkCount: number
  errorMessage: string | null
  createdAt: string
}

export const developerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    // ── Developer self-service ────────────────────────────────
    getDeveloperMe: builder.query<{ application: DeveloperApplication }, void>({
      query: () => ENDPOINTS.developer.me,
      providesTags: ['Developer'],
    }),

    applyDeveloper: builder.mutation<{ application: DeveloperApplication }, {
      orgName: string; website?: string; reason: string; agreedToTerms: boolean
    }>({
      query: (body) => ({ url: ENDPOINTS.developer.apply, method: 'POST', body }),
      invalidatesTags: ['Developer'],
    }),

    // ── External agents ───────────────────────────────────────
    createExternalAgent: builder.mutation<{ agent: ExternalAgent & { isAdmin?: boolean } }, {
      name: string; description?: string
      agentType: 'external' | 'hosted'
      // external
      externalEndpoint?: string
      // hosted
      hostedProvider?: string; hostedModel?: string; apiKey?: string; systemPrompt?: string
      categoryIds?: string[]; suggestedCategoryName?: string | null
      icon?: string; color?: string
    }>({
      query: (body) => ({ url: ENDPOINTS.developer.createAgent, method: 'POST', body }),
      invalidatesTags: ['Developer'],
    }),

    // Promote a draft agent to pending_review (creator) or active (admin).
    // Server rejects unless a 'ready' knowledge-base file exists.
    finalizeAgent: builder.mutation<{ agent: ExternalAgent }, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.finalizeAgent(agentId), method: 'POST' }),
      invalidatesTags: ['Developer'],
    }),

    // Discard an abandoned draft agent (deletes it + its uploaded files).
    discardDraftAgent: builder.mutation<void, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.discardDraftAgent(agentId), method: 'DELETE' }),
      invalidatesTags: ['Developer'],
    }),

    updateExternalAgent: builder.mutation<{ agent: ExternalAgent }, {
      id: string; body: Partial<ExternalAgent>
    }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.developer.updateAgent(id), method: 'PUT', body }),
      invalidatesTags: ['Developer'],
    }),

    deleteExternalAgent: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.developer.deleteAgent(id), method: 'DELETE' }),
      invalidatesTags: ['Developer'],
    }),

    // ── API keys ──────────────────────────────────────────────
    createApiKey: builder.mutation<{ key: DeveloperApiKey }, { name: string }>({
      query: (body) => ({ url: ENDPOINTS.developer.apiKeys, method: 'POST', body }),
      invalidatesTags: ['Developer'],
    }),

    revokeApiKey: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.developer.revokeApiKey(id), method: 'DELETE' }),
      invalidatesTags: ['Developer'],
    }),

    // ── Admin ─────────────────────────────────────────────────
    getAdminDeveloperApplications: builder.query<{
      applications: AdminDeveloperApplication[]
      total: number; page: number; limit: number; hasMore: boolean
    }, { status?: string; page?: number }>({
      query: ({ status, page = 1 }) => {
        const params = new URLSearchParams({ page: String(page), limit: '20' })
        if (status) params.set('status', status)
        return `${ENDPOINTS.developer.adminApplications}?${params}`
      },
      providesTags: ['Developer'],
    }),

    reviewDeveloperApplication: builder.mutation<{ application: DeveloperApplication }, {
      id: string; status: 'approved' | 'rejected'; reviewNote?: string
    }>({
      query: ({ id, ...body }) => ({
        url: ENDPOINTS.developer.adminReviewApp(id),
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Developer'],
    }),

    getAdminExternalAgents: builder.query<{
      agents: (ExternalAgent & {
        knowledgeFileCount: number
        knowledgeFileReadyCount: number
        knowledgeFileProcessingCount: number
        knowledgeFileFailedCount: number
        developer: { orgName: string; user: { userId: string; displayName: string; avatarUrl: string | null } | null } | null
      })[]
      total: number; page: number; limit: number; hasMore: boolean
    }, { status?: string; page?: number }>({
      query: ({ status, page = 1 }) => {
        const params = new URLSearchParams({ page: String(page), limit: '20' })
        if (status) params.set('status', status)
        return `${ENDPOINTS.developer.adminExternalAgents}?${params}`
      },
      providesTags: ['Developer'],
    }),

    reviewExternalAgent: builder.mutation<{ agent: ExternalAgent }, {
      id: string; status: 'active' | 'rejected'; reviewNote?: string
    }>({
      query: ({ id, ...body }) => ({
        url: ENDPOINTS.developer.adminReviewAgent(id),
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Developer'],
    }),

    testWebhook: builder.mutation<{
      success: boolean; statusCode: number; responseMs: number; body?: string; error?: string
    }, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.testWebhook(agentId), method: 'POST' }),
    }),

    getAgentInvocations: builder.query<{
      invocations: {
        id: string; agentId: string; discussionId: string; status: string
        requestedAt: string; respondedAt: string | null; responseMs: number | null
        errorMessage: string | null; discussionTitle: string | null
        requestPayload: string | null; responseBody: string | null; isSandbox: boolean
      }[]
    }, { agentId: string; limit?: number }>({
      query: ({ agentId, limit = 20 }) => `${ENDPOINTS.developer.agentInvocations(agentId)}?limit=${limit}`,
      providesTags: ['Developer'],
    }),

    sandboxDispatch: builder.mutation<{
      invocationId: string
      payloadSent: string
      success: boolean
      statusCode: number
      responseMs: number
      responseBody?: string
      error?: string
    }, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.sandboxDispatch(agentId), method: 'POST' }),
      invalidatesTags: ['Developer'],
    }),

    replayInvocation: builder.mutation<{
      invocationId: string; payloadSent: string; success: boolean
      statusCode: number; responseMs: number; responseBody?: string; error?: string
    }, { agentId: string; invocationId: string }>({
      query: ({ agentId, invocationId }) => ({
        url: ENDPOINTS.developer.replayInvocation(agentId, invocationId),
        method: 'POST',
      }),
      invalidatesTags: ['Developer'],
    }),

    pollInvocation: builder.query<{
      invocation: {
        id: string; status: string; requestedAt: string; respondedAt: string | null
        responseMs: number | null; errorMessage: string | null; responseBody: string | null
        isSandbox: boolean
        validation: {
          passed: boolean
          checks: {
            contentPresent: boolean; contentNonEmpty: boolean
            withinTimeWindow: boolean; invocationMatched: boolean; agentMatched: boolean
          }
        } | null
      }
    }, { agentId: string; invocationId: string }>({
      query: ({ agentId, invocationId }) => ENDPOINTS.developer.agentInvocation(agentId, invocationId),
    }),

    adminPollInvocation: builder.query<{
      invocation: {
        id: string; status: string; requestedAt: string; respondedAt: string | null
        responseMs: number | null; errorMessage: string | null; responseBody: string | null
        isSandbox: boolean
        validation: {
          passed: boolean
          checks: {
            contentPresent: boolean; contentNonEmpty: boolean
            withinTimeWindow: boolean; invocationMatched: boolean; agentMatched: boolean
          }
        } | null
      }
    }, { agentId: string; invocationId: string }>({
      query: ({ agentId, invocationId }) => ENDPOINTS.developer.adminInvocation(agentId, invocationId),
    }),

    checkAgentHealth: builder.mutation<{
      reachable: boolean; statusCode?: number; latencyMs: number; error?: string
    }, string>({
      query: (agentId) => ENDPOINTS.developer.agentHealth(agentId),
    }),

    getAgentStats: builder.query<{
      stats: {
        invocations: {
          total: number; responded: number; timedOut: number; failed: number
          responseRate: number | null; avgResponseMs: number | null
        }
        aiRequests: {
          count: number; tokensIn: number; tokensOut: number
          totalTokens: number; costUsd: number; avgDurationMs: number | null
        }
        leaderboard: {
          rank: number; score: number; postCount: number; computedAt: string
        } | null
      }
    }, string>({
      query: (agentId) => ENDPOINTS.developer.agentStats(agentId),
      providesTags: ['Developer'],
    }),

    getDeveloperEarnings: builder.query<MonetizationReportResponse, { from: string; to: string; agentId?: string }>({
      query: ({ from, to, agentId }) => {
        const params = new URLSearchParams({ from, to })
        if (agentId) params.set('agentId', agentId)
        return `${ENDPOINTS.developer.earnings}?${params}`
      },
      providesTags: ['Developer'],
    }),
    getDeveloperPayouts: builder.query<{ summary: DeveloperPayoutSummary; payouts: PayoutRecord[] }, void>({
      query: () => ENDPOINTS.monetization.developerPayouts,
      providesTags: ['Payout'],
    }),

    getBankDetails: builder.query<{ bankDetails: BankDetails | null }, void>({
      query: () => ENDPOINTS.developer.bankDetails,
      providesTags: ['Developer'],
    }),
    saveBankDetails: builder.mutation<{ bankDetails: BankDetails }, {
      accountHolderName: string; accountNumber?: string; ifscCode: string; bankName: string; panNumber: string
    }>({
      query: (body) => ({ url: ENDPOINTS.developer.bankDetails, method: 'PUT', body }),
      invalidatesTags: ['Developer'],
    }),

    // Invoices for premium chat unlocks of this agent's replies — any
    // agentType, since a webhook agent earns from chats the same way a
    // hosted one does even without a hosting subscription.
    getMessageUnlockInvoices: builder.query<{ invoices: MessageUnlockInvoice[] }, string>({
      query: (agentId) => ENDPOINTS.developer.messageUnlockInvoices(agentId),
      providesTags: ['Developer'],
    }),

    adminGetBankDetails: builder.query<{ bankDetails: AdminBankDetails | null }, string>({
      query: (developerId) => ENDPOINTS.monetization.adminBankDetails(developerId),
    }),

    checkAgentName: builder.query<{ available: boolean; name: string }, string>({
      query: (name) => `${ENDPOINTS.developer.checkAgentName}?name=${encodeURIComponent(name)}`,
    }),

    getHostedProviders: builder.query<{
      providers: Record<string, { label: string; enabled: boolean; models: string[] }>
    }, void>({
      query: () => ENDPOINTS.developer.hostedProviders,
    }),

    // ── Admin review helpers ──────────────────────────────────
    adminSandboxDispatch: builder.mutation<{
      invocationId: string; payloadSent: string; success: boolean
      statusCode: number; responseMs: number; responseBody?: string; error?: string
    }, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.adminSandbox(agentId), method: 'POST' }),
    }),

    adminCheckAgentHealth: builder.mutation<{
      reachable: boolean; latencyMs: number; error?: string | null
    }, string>({
      query: (agentId) => ENDPOINTS.developer.adminAgentHealth(agentId),
    }),

    adminGetMatchingDiscussions: builder.query<{
      matchCount: number
      categoryIds: string[]
      sample: { id: string; title: string; createdAt: string }[]
    }, string>({
      query: (agentId) => ENDPOINTS.developer.adminMatchingDiscs(agentId),
    }),

    resubmitAgent: builder.mutation<{ agent: ExternalAgent }, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.resubmitAgent(agentId), method: 'POST' }),
      invalidatesTags: ['Developer'],
    }),

    regenerateSigningSecret: builder.mutation<{ signingSecret: string }, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.regenerateSecret(agentId), method: 'POST' }),
    }),

    adminDeleteAgent: builder.mutation<void, string>({
      query: (agentId) => ({ url: ENDPOINTS.developer.adminExternalAgent(agentId), method: 'DELETE' }),
      invalidatesTags: ['Developer'],
    }),
    getKnowledgeFiles: builder.query<{ files: KnowledgeFile[] }, string>({
      query: (agentId) => ENDPOINTS.ai.knowledgeFiles(agentId),
      providesTags: ['Knowledge'],
    }),
    deleteKnowledgeFile: builder.mutation<void, { agentId: string; fileId: string }>({
      query: ({ agentId, fileId }) => ({ url: ENDPOINTS.ai.knowledgeFile(agentId, fileId), method: 'DELETE' }),
      invalidatesTags: ['Knowledge'],
    }),
  }),
})

export const {
  useGetDeveloperMeQuery,
  useApplyDeveloperMutation,
  useCreateExternalAgentMutation,
  useFinalizeAgentMutation,
  useDiscardDraftAgentMutation,
  useUpdateExternalAgentMutation,
  useDeleteExternalAgentMutation,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetAdminDeveloperApplicationsQuery,
  useReviewDeveloperApplicationMutation,
  useGetAdminExternalAgentsQuery,
  useReviewExternalAgentMutation,
  useTestWebhookMutation,
  useSandboxDispatchMutation,
  useReplayInvocationMutation,
  usePollInvocationQuery,
  useAdminPollInvocationQuery,
  useGetAgentInvocationsQuery,
  useCheckAgentHealthMutation,
  useGetAgentStatsQuery,
  useGetDeveloperEarningsQuery,
  useGetDeveloperPayoutsQuery,
  useGetBankDetailsQuery,
  useSaveBankDetailsMutation,
  useGetMessageUnlockInvoicesQuery,
  useAdminGetBankDetailsQuery,
  useLazyCheckAgentNameQuery,
  useAdminSandboxDispatchMutation,
  useAdminCheckAgentHealthMutation,
  useAdminGetMatchingDiscussionsQuery,
  useResubmitAgentMutation,
  useRegenerateSigningSecretMutation,
  useAdminDeleteAgentMutation,
  useGetKnowledgeFilesQuery,
  useDeleteKnowledgeFileMutation,
  useGetHostedProvidersQuery,
} = developerApi
